import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GamificationMetricKey, GamificationPeriodKey } from "./types";

export type PopupTier = GamificationPeriodKey | "aspiration";

export interface GoalAchievementPopupProps {
  title: string;
  description: string;
  tier: PopupTier;
  metric?: GamificationMetricKey;
  duration?: number;
  onClose: () => void;
}

const TIER_CONFIG: Record<
  PopupTier,
  { label: string; gradient: string; badge: string; bar: string }
> = {
  daily: {
    label: "Daily Goal",
    gradient: "from-blue-500 to-blue-600",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
    bar: "from-blue-400 to-blue-600",
  },
  weekly: {
    label: "Weekly Milestone",
    gradient: "from-purple-500 to-purple-600",
    badge: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
    bar: "from-purple-400 to-purple-600",
  },
  monthly: {
    label: "Monthly Milestone",
    gradient: "from-amber-500 to-amber-600",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    bar: "from-amber-400 to-amber-600",
  },
  aspiration: {
    label: "Goal Complete",
    gradient: "from-emerald-500 to-emerald-600",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
    bar: "from-emerald-400 to-emerald-600",
  },
};

const METRIC_EMOJI: Partial<Record<GamificationMetricKey, string>> = {
  wins: "🏆",
  activity: "📞",
  pipeline: "🎯",
  revenueBooked: "💰",
};

export function GoalAchievementPopup({
  title,
  description,
  tier,
  metric,
  duration = 6000,
  onClose,
}: GoalAchievementPopupProps) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = () => {
    if (exiting) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setExiting(true);
    exitTimeoutRef.current = setTimeout(onClose, 380);
  };

  useEffect(() => {
    timerRef.current = setTimeout(dismiss, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    };
  }, []);

  const cfg = TIER_CONFIG[tier] ?? TIER_CONFIG.daily;
  const emoji = (metric && METRIC_EMOJI[metric]) ?? (tier === "aspiration" ? "⭐" : "🎉");

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed left-1/2 -translate-x-1/2 z-[9998]",
        "top-[12%] sm:top-[10%]",
        "w-[min(400px,calc(100vw-2rem))]",
        "pointer-events-auto",
        exiting
          ? "animate-out fade-out slide-out-to-top-4 duration-[380ms]"
          : "animate-in fade-in slide-in-from-bottom-12 zoom-in-[0.92] duration-500",
      )}
      style={{ willChange: "transform, opacity" }}
    >
      <div className="overflow-hidden rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.18)] bg-white dark:bg-gray-900 ring-1 ring-black/8 dark:ring-white/10">
        <div className={cn("h-1.5 w-full bg-gradient-to-r", cfg.gradient)} />

        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex items-center justify-center w-12 h-12 rounded-xl text-2xl bg-gradient-to-br shadow-md flex-shrink-0",
                  cfg.gradient,
                )}
              >
                {emoji}
              </div>
              <div className="min-w-0">
                <span
                  className={cn(
                    "inline-block text-[11px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full mb-1",
                    cfg.badge,
                  )}
                >
                  {cfg.label}
                </span>
                <h3 className="text-[15px] font-bold text-gray-900 dark:text-white leading-snug">
                  {title}
                </h3>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0 -mt-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <p className="mt-2.5 text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
            {description}
          </p>
        </div>

        <div className="h-[3px] bg-gray-100 dark:bg-gray-800">
          <div
            className={cn("h-full bg-gradient-to-r origin-left", cfg.bar)}
            style={{
              animation: `goal-popup-shrink ${duration}ms linear forwards`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
